import {FeedFiles, Preview} from '@/api/models/FeedFiles.ts';
import {FeedField, FIELD_NAMES, Mapper as MapperEntity, MAPPER_NAMES, MapperKind} from '@/api/models/Feeds.ts';
import Pending from '@/components/Pending.tsx';
import {FeedFileStub} from '@/components/Selects/FeedFileSelect.tsx';
import {Controls} from '@/pages/feeds/Editor/Mapper/Controls.tsx';
import {FieldDialog, SaveFn} from '@/pages/feeds/Editor/Mapper/FieldDialog.tsx';
import MappingDrawer, {MappingChangeFn} from '@/pages/feeds/Editor/Mapper/MappingDrawer.tsx';
import {Button, HTMLTable, NonIdealState} from '@blueprintjs/core';
import {ReactElement, useCallback, useMemo, useState} from 'react';

export type MemoryFeedField = Omit<FeedField, 'id' | 'feed'>

interface Props {
	file: FeedFileStub | null,
	fields: MemoryFeedField[],
	onFieldsChange: (fields: MemoryFeedField[]) => void,
}

export default function Mapper({file, fields, onFieldsChange}: Props): ReactElement {
	const preview = useMemo(() => {
		if (file)
			return FeedFiles.preview(file.id);
		else
			return Promise.resolve(null);
	}, [file?.id]);

	return (
		<Pending resource={preview}>
			{preview => <Fields fields={fields} onFieldsChange={onFieldsChange} preview={preview} />}
		</Pending>
	);
}

interface FieldsProps {
	preview: Preview | null,
	fields: MemoryFeedField[],
	onFieldsChange: (fields: MemoryFeedField[]) => void,
}

function Fields({preview, fields, onFieldsChange}: FieldsProps): ReactElement {
	const [showFieldDialog, setShowFieldDialog] = useState(false);

	const onAddFieldClick = useCallback(() => setShowFieldDialog(true), []);
	const onFieldDialogClose = useCallback(() => setShowFieldDialog(false), []);

	const onFieldDialogSave: SaveFn = useCallback(field => {
		const newFields = [...fields];
		const index = fields.findIndex(f => f.schemaKey === field.schemaKey);

		if (index === -1)
			newFields.push(field);
		else
			newFields[index] = field;

		setShowFieldDialog(false);
		onFieldsChange(newFields);
	}, [fields, onFieldsChange]);

	const onFieldDelete = useCallback((field: MemoryFeedField) => {
		onFieldsChange(fields.filter(v => v !== field));
	}, [fields, onFieldsChange]);

	const [mappingTarget, setMappingTarget] = useState<MemoryFeedField | null>(null);

	const onMappingDrawerClose = useCallback(() => {
		setMappingTarget(null);
	}, []);

	const onMappingDrawerChange: MappingChangeFn = useCallback(_field => {
		onFieldsChange([...fields]);
	}, [onFieldsChange, fields]);

	if (!preview) {
		return (
			<div style={{margin: '20px 0'}}>
				<NonIdealState
					layout="horizontal"
					icon="property"
					title="No Source File Selected"
					description={(
						<>
							Select a file from the dropdown above<br />
							to begin mapping fields.
						</>
					)}
				/>
			</div>
		);
	}

	return (
		<>
			<Button intent="primary" icon="plus" text="Add Field" onClick={onAddFieldClick} />

			{fields.length > 0 && (
				<HTMLTable striped={true} interactive={true} style={{width: '100%'}}>
					<thead>
						<tr>
							<th>Feed Column</th>
							<th>Schema Field</th>
							<th>Manipulation</th>
							<th style={{width: 100}}>&nbsp;</th>
						</tr>
					</thead>

					<tbody>
						{fields.map(field => (
							<tr key={field.schemaKey}>
								<td>{field.feedKey}</td>
								<td>{FIELD_NAMES[field.schemaKey]}</td>
								<td>{getDisplayValue(field.mapper)}</td>
								<td>
									<Controls
										field={field}
										onDelete={onFieldDelete}
										onShowMapping={setMappingTarget}
									/>
								</td>
							</tr>
						))}
					</tbody>
				</HTMLTable>
			)}

			{showFieldDialog && (
				<FieldDialog
					keys={preview.keys}
					sample={preview.contents[0]}
					onClose={onFieldDialogClose}
					onSave={onFieldDialogSave}
				/>
			)}

			{mappingTarget && (
				<MappingDrawer field={mappingTarget} onClose={onMappingDrawerClose} onChange={onMappingDrawerChange} />
			)}
		</>
	);
}

function getDisplayValue(mapper: MapperEntity | null): string {
	if (!mapper)
		return '—';

	switch (mapper.kind) {
		case MapperKind.Chain:
			const names = mapper.mappers.map(getDisplayValue);
			return names.join(' → ');

		default:
			return MAPPER_NAMES[mapper.kind];
	}
}
